import React, { useState, useEffect } from 'react';
import {
  Mail, Star, Trash2, Check, CheckCheck, Search,
  MailOpen, X, ExternalLink, Reply, Clock, RefreshCw
} from 'lucide-react';
import { useApi } from '../AdminApp';

const MessagesPage = () => {
  const { apiCall } = useApi();
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [counts, setCounts] = useState({ total: 0, unread: 0, starred: 0 });
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // all, unread, starred
  const [selectedIds, setSelectedIds] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchMessages();
  }, [filter]);

  const fetchMessages = async () => {
    try {
      const params = new URLSearchParams();
      if (filter === 'unread') params.append('read', 'false');
      if (filter === 'starred') params.append('starred', 'true');

      const data = await apiCall(`/messages?${params.toString()}`);
      setMessages(data.messages);
      setCounts(data.counts);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const openMessage = async (msg) => {
    setSelectedMessage(msg);
    if (!msg.read) {
      try {
        await apiCall(`/messages/${msg._id}/read`, { method: 'PATCH' });
        fetchMessages();
      } catch (error) {
        console.error('Error marking as read:', error);
      }
    }
  };

  const toggleStar = async (msg, e) => {
    e?.stopPropagation();
    try {
      await apiCall(`/messages/${msg._id}/star`, { method: 'PATCH' });
      fetchMessages();
      if (selectedMessage?._id === msg._id) {
        setSelectedMessage(prev => ({ ...prev, starred: !prev.starred }));
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const deleteMessage = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      await apiCall(`/messages/${id}`, { method: 'DELETE' });
      fetchMessages();
      if (selectedMessage?._id === id) {
        setSelectedMessage(null);
      }
      setMessage({ type: 'success', text: 'Message deleted successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const markAllRead = async () => {
    try {
      await apiCall('/messages/mark-all-read', { method: 'POST' });
      fetchMessages();
      setMessage({ type: 'success', text: 'All messages marked as read!' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected messages?`)) return;

    try {
      await apiCall('/messages/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ ids: selectedIds })
      });
      setSelectedIds([]);
      fetchMessages();
      setMessage({ type: 'success', text: 'Messages deleted successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const filteredMessages = messages.filter(msg =>
    msg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    msg.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    msg.message.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">Messages</h1>
          <p className="text-gray-400 mt-1">
            {counts.unread} unread of {counts.total} total messages
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchMessages}
            className="flex items-center gap-2 px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-xl transition"
          >
            <RefreshCw className="w-5 h-5" />
            Refresh
          </button>
          {counts.unread > 0 && (
            <button
              onClick={markAllRead}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-xl transition"
            >
              <CheckCheck className="w-5 h-5" />
              Mark All Read
            </button>
          )}
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-4 rounded-xl ${message.type === 'success' ? 'bg-green-500/10 border border-green-500/50 text-green-400' : 'bg-red-500/10 border border-red-500/50 text-red-400'}`}>
          {message.text}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <button
          onClick={() => setFilter('all')}
          className={`p-4 rounded-xl border transition ${filter === 'all' ? 'bg-blue-500/20 border-blue-500/50 text-blue-400' : 'bg-gray-800/50 border-gray-700/50 text-gray-400 hover:text-white'}`}
        >
          <Mail className="w-6 h-6 mx-auto mb-2" />
          <p className="text-2xl font-bold">{counts.total}</p>
          <p className="text-sm">Total</p>
        </button>
        <button
          onClick={() => setFilter('unread')}
          className={`p-4 rounded-xl border transition ${filter === 'unread' ? 'bg-orange-500/20 border-orange-500/50 text-orange-400' : 'bg-gray-800/50 border-gray-700/50 text-gray-400 hover:text-white'}`}
        >
          <MailOpen className="w-6 h-6 mx-auto mb-2" />
          <p className="text-2xl font-bold">{counts.unread}</p>
          <p className="text-sm">Unread</p>
        </button>
        <button
          onClick={() => setFilter('starred')}
          className={`p-4 rounded-xl border transition ${filter === 'starred' ? 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400' : 'bg-gray-800/50 border-gray-700/50 text-gray-400 hover:text-white'}`}
        >
          <Star className="w-6 h-6 mx-auto mb-2" />
          <p className="text-2xl font-bold">{counts.starred}</p>
          <p className="text-sm">Starred</p>
        </button>
      </div>

      {/* Search & Actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search messages..."
            className="w-full pl-12 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
          />
        </div>
        {selectedIds.length > 0 && (
          <button
            onClick={bulkDelete}
            className="flex items-center gap-2 px-4 py-3 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-xl transition"
          >
            <Trash2 className="w-5 h-5" />
            Delete ({selectedIds.length})
          </button>
        )}
      </div>

      {/* Messages List */}
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 overflow-hidden">
        {filteredMessages.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No messages found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-700/50">
            {filteredMessages.map((msg) => (
              <div
                key={msg._id}
                onClick={() => openMessage(msg)}
                className={`flex items-center gap-4 p-4 cursor-pointer transition hover:bg-gray-700/30 ${!msg.read ? 'bg-blue-500/5' : ''}`}
              >
                {/* Checkbox */}
                <input
                  type="checkbox"
                  checked={selectedIds.includes(msg._id)}
                  onChange={(e) => { e.stopPropagation(); toggleSelect(msg._id); }}
                  className="w-5 h-5 rounded border-gray-600 text-blue-500 focus:ring-blue-500 bg-gray-700"
                />

                {/* Star */}
                <button
                  onClick={(e) => toggleStar(msg, e)}
                  className={`p-1 rounded transition ${msg.starred ? 'text-yellow-400' : 'text-gray-500 hover:text-yellow-400'}`}
                >
                  <Star className="w-5 h-5" fill={msg.starred ? 'currentColor' : 'none'} />
                </button>

                {/* Avatar */}
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  {msg.name?.charAt(0).toUpperCase() || '?'}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`font-medium truncate ${!msg.read ? 'text-white' : 'text-gray-300'}`}>
                      {msg.name}
                    </p>
                    {!msg.read && (
                      <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                    )}
                  </div>
                  <p className="text-sm text-gray-400 truncate">{msg.email}</p>
                  <p className={`text-sm mt-1 truncate ${!msg.read ? 'text-gray-200' : 'text-gray-400'}`}>
                    {msg.subject ? `${msg.subject}: ` : ''}{msg.message}
                  </p>
                </div>

                {/* Date & Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-sm text-gray-500 hidden sm:block">
                    {new Date(msg.createdAt).toLocaleDateString()}
                  </span>
                  <button
                    onClick={(e) => deleteMessage(msg._id, e)}
                    className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition opacity-0 group-hover:opacity-100"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Message Detail Modal */}
      {selectedMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setSelectedMessage(null)} />
          <div className="relative bg-gray-900 rounded-2xl border border-gray-700 w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {selectedMessage.name?.charAt(0).toUpperCase() || '?'}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">{selectedMessage.name}</h3>
                  <a href={`mailto:${selectedMessage.email}`} className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1">
                    {selectedMessage.email}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => toggleStar(selectedMessage, e)}
                  className={`p-2 rounded-xl transition ${selectedMessage.starred ? 'text-yellow-400 bg-yellow-500/20' : 'text-gray-400 hover:text-yellow-400 hover:bg-gray-700'}`}
                >
                  <Star className="w-5 h-5" fill={selectedMessage.starred ? 'currentColor' : 'none'} />
                </button>
                <button
                  onClick={(e) => deleteMessage(selectedMessage._id, e)}
                  className="p-2 text-red-400 hover:bg-red-500/20 rounded-xl transition"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setSelectedMessage(null)}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-xl transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {selectedMessage.subject && (
                <h4 className="text-xl font-medium text-white mb-4">{selectedMessage.subject}</h4>
              )}
              <p className="text-gray-300 whitespace-pre-wrap leading-relaxed">{selectedMessage.message}</p>

              <div className="flex items-center gap-4 mt-6 pt-6 border-t border-gray-700 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {new Date(selectedMessage.createdAt).toLocaleString()}
                </span>
                {selectedMessage.replied && (
                  <span className="flex items-center gap-1 text-green-400">
                    <Check className="w-4 h-4" />
                    Replied
                  </span>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-700">
              <a
                href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject || 'Your message'}`}
                className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition"
              >
                <Reply className="w-5 h-5" />
                Reply via Email
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesPage;
